import { Pipe, PipeTransform } from '@angular/core';
import { marked, Tokens } from 'marked';

/**
 * One rendered block of an Emily reply. A single streamed answer is split into an ordered
 * list of these so prose renders in bubbles while tables and notes render bare.
 * - `bubble` — prose (summary, suggested actions, …) shown in a gray chat bubble.
 * - `table`  — a markdown table, rendered bare with a copy affordance offering both
 *             CSV (`csv`) and Excel/TSV (`tsv`) clipboard formats.
 * - `note`   — a `*Note: …*`-style paragraph, rendered bare as a callout.
 */
export type EmilySegment =
  | { kind: 'bubble'; html: string }
  | { kind: 'table'; html: string; csv: string; tsv: string }
  | { kind: 'note'; html: string };

const OPTS = { async: false as const, breaks: true, gfm: true };

// RFC-4180 cell escaping: wrap in quotes and double embedded quotes when the cell
// contains a quote, comma, or newline.
const escapeCsv = (value: string): string =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const tableToCsv = (token: Tokens.Table): string => {
  const header = token.header.map((cell) => escapeCsv(cell.text)).join(',');
  const body = token.rows.map((row) => row.map((cell) => escapeCsv(cell.text)).join(','));
  return [header, ...body].join('\n');
};

// Tab-separated for spreadsheet paste (Excel/Sheets): tabs split columns, newlines
// split rows. A literal tab/newline inside a cell would break the grid, so collapse
// any whitespace-control chars to a single space.
const sanitizeTsv = (value: string): string => value.replace(/[\t\r\n]+/g, ' ');

const tableToTsv = (token: Tokens.Table): string => {
  const header = token.header.map((cell) => sanitizeTsv(cell.text)).join('\t');
  const body = token.rows.map((row) => row.map((cell) => sanitizeTsv(cell.text)).join('\t'));
  return [header, ...body].join('\n');
};

@Pipe({ name: 'emilySegments', standalone: true })
export class EmilySegmentsPipe implements PipeTransform {
  transform(value: string | null | undefined): EmilySegment[] {
    const tokens = marked.lexer(value ?? '', OPTS);
    const segments: EmilySegment[] = [];
    let buffer = '';

    // Flush accumulated prose into a bubble segment.
    const flush = (): void => {
      if (buffer.trim()) {
        segments.push({ kind: 'bubble', html: marked.parse(buffer, OPTS) as string });
      }
      buffer = '';
    };

    for (const token of tokens) {
      if (token.type === 'table') {
        flush();
        segments.push({
          kind: 'table',
          html: marked.parse(token.raw, OPTS) as string,
          csv: tableToCsv(token as Tokens.Table),
          tsv: tableToTsv(token as Tokens.Table),
        });
        continue;
      }
      // Content-based note detection: paragraph whose text starts with "Note"
      // (allowing leading emphasis markers like *Note: …*). The backend sends no
      // structural marker, so this is the most robust signal available.
      if (token.type === 'paragraph' && /^\s*[*_]*note\b/i.test(token.text)) {
        flush();
        segments.push({ kind: 'note', html: marked.parse(token.raw, OPTS) as string });
        continue;
      }
      buffer += token.raw; // 'space' tokens land here too — harmless
    }
    flush();

    return segments;
  }
}
